import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, TextField, MenuItem, ToggleButton, ToggleButtonGroup, Paper, Typography
} from "@mui/material";
import { Refresh, OpenInNew } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { aiCallApi, useAICallCampaigns, CAMPAIGN_STATUS } from "./useAICalling";
import { AICallingPage, Loading, ErrorState } from "./AICallingComponents";
import { CampaignReport, OverallReport } from "./AICallReportView";

const PERIODS = [7, 30, 90];

const AICallingReports = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const campaignId = params.get("campaignId") || "";
  const daysParam = parseInt(params.get("days"), 10);
  const days = PERIODS.includes(daysParam) ? daysParam : 30;

  const { campaigns, fetch: fetchCampaigns } = useAICallCampaigns();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadOverall = useCallback(async () => {
    setLoading(true);
    const res = await aiCallApi.report({ days });
    setLoading(false);
    if (!res.ok) return setError(res.error);
    setError("");
    setReport(res.data);
  }, [days]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);
  useEffect(() => {
    if (!campaignId) loadOverall();
  }, [campaignId, loadOverall]);

  const launched = campaigns.filter((c) => c.status !== "draft");
  const selected = campaigns.find((c) => c._id === campaignId);

  const setParam = (changes) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v === "" || v === null || v === undefined) next.delete(k);
      else next.set(k, String(v));
    }
    setParams(next);
  };

  return (
    <AICallingPage
      title="AI Calling Reports"
      subtitle="Did the calls bring in more money than they cost?"
      actions={!campaignId ? <Button variant="outlined" startIcon={<Refresh />} onClick={loadOverall} disabled={loading}>Refresh</Button> : undefined}
    >
      <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          select
          size="small"
          label="Show"
          value={campaignId}
          onChange={(e) => setParam({ campaignId: e.target.value })}
          sx={{ minWidth: 260, flex: "1 1 260px", maxWidth: 480 }}
        >
          <MenuItem value="">All calls (by period)</MenuItem>
          {launched.map((c) => (
            <MenuItem key={c._id} value={c._id}>
              {c.name} · {CAMPAIGN_STATUS[c.status]?.label || c.status}
            </MenuItem>
          ))}
          {campaignId && !selected && <MenuItem value={campaignId}>This campaign</MenuItem>}
        </TextField>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">Period</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            disabled={!!campaignId}
            onChange={(_, v) => v && setParam({ days: v })}
          >
            {PERIODS.map((d) => <ToggleButton key={d} value={d}>{d} days</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
        {campaignId && (
          <Button size="small" startIcon={<OpenInNew />} onClick={() => navigate(`/ai-calling/campaigns/${campaignId}`)}>
            Open campaign
          </Button>
        )}
      </Paper>

      {campaignId ? (
        <CampaignReport campaignId={campaignId} title={selected ? `${selected.name}: results` : "Campaign results"} />
      ) : loading && !report ? (
        <Loading text="Building the report..." />
      ) : error && !report ? (
        <ErrorState error={error} onRetry={loadOverall} />
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1 }}>
          <OverallReport report={report} onReload={loadOverall} />
        </Box>
      )}
    </AICallingPage>
  );
};

export default AICallingReports;
